/**
 * Calcoli di supporto al calendario disponibilità.
 * Solo funzioni pure sulle occupazioni restituite da
 * BookingEngine::getAvailability(): nessuna chiamata di rete.
 *
 * Gli orari di una giornata sono trattati come minuti dalla mezzanotte,
 * così sovrapposizioni e spazi liberi si calcolano con semplici interi.
 */
import { GIORNI, MESI, oraInMinuti, parseSql } from '../../lib/format';

/** Passo, in minuti, con cui si propongono gli orari di inizio. */
export const STEP_MINUTI = 30;

/** Si prenota a ore intere, almeno una: sotto l'ora un buco non serve. */
export const DURATA_MINIMA = 60;


/**
 * 'YYYY-MM-DD' -> mezzanotte locale.
 * `parseSql` su una data secca la leggerebbe come UTC, spostando il giorno.
 */
export function inizioGiornata(key) {
    const m = String(key || '').match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3]);
}

/** 'YYYY-MM-DD' -> 'Lunedi 8 settembre' */
export function labelGiorno(key) {
    const d = inizioGiornata(key);
    if (!d) return key || '—';
    return `${GIORNI[(d.getDay() + 6) % 7]} ${d.getDate()} ${MESI[d.getMonth()].toLowerCase()}`;
}

/** minuti dalla mezzanotte -> 'HH:MM' */
export function minutesToTime(min) {
    const h = String(Math.floor(min / 60)).padStart(2, '0');
    return `${h}:${String(min % 60).padStart(2, '0')}`;
}

/** Chiave 'YYYY-MM', per confrontare i mesi. */
export function monthKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function addMonths(date, n) {
    return new Date(date.getFullYear(), date.getMonth() + n, 1);
}

/** Celle del mese, per settimane che partono da lunedì. */
export function monthMatrix(month) {
    const primo = new Date(month.getFullYear(), month.getMonth(), 1);
    const scarto = (primo.getDay() + 6) % 7;
    const giorniMese = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const settimane = Math.ceil((scarto + giorniMese) / 7);

    const celle = [];
    for (let i = 0; i < settimane * 7; i++) {
        celle.push(new Date(month.getFullYear(), month.getMonth(), 1 - scarto + i));
    }
    return celle;
}

/** Lunedi' della settimana che contiene `date`. */
export function inizioSettimana(date) {
    const scarto = (date.getDay() + 6) % 7;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - scarto);
}

export function addWeeks(date, n) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n * 7);
}

/** I sette giorni della settimana che contiene `date`, da lunedi'. */
export function weekMatrix(date) {
    const lunedi = inizioSettimana(date);
    const giorni = [];
    for (let i = 0; i < 7; i++) {
        giorni.push(new Date(lunedi.getFullYear(), lunedi.getMonth(), lunedi.getDate() + i));
    }
    return giorni;
}

/** '22 – 28 settembre 2026', accorciato quando la settimana sta in un mese solo. */
export function labelSettimana(date) {
    const giorni = weekMatrix(date);
    const da = giorni[0];
    const a = giorni[6];
    const mese = (d) => MESI[d.getMonth()].toLowerCase();
    if (da.getFullYear() !== a.getFullYear()) {
        return `${da.getDate()} ${mese(da)} ${da.getFullYear()} – ${a.getDate()} ${mese(a)} ${a.getFullYear()}`;
    }
    if (da.getMonth() !== a.getMonth()) {
        return `${da.getDate()} ${mese(da)} – ${a.getDate()} ${mese(a)} ${a.getFullYear()}`;
    }
    return `${da.getDate()} – ${a.getDate()} ${mese(a)} ${a.getFullYear()}`;
}

/**
 * La griglia oraria della settimana e' fatta di ore intere: quelle che
 * bastano a coprire l'orario della sala. Una sala aperta dalle 8:30 parte
 * comunque dalle 8:00, o la prima fascia sarebbe alta meno delle altre.
 */
export function fasceOrarie(apertura, chiusura) {
    const base = Math.floor((apertura || 0) / 60);
    const fine = Math.max(base + 1, Math.ceil((chiusura || 0) / 60));
    const ore = [];
    for (let h = base; h <= fine; h++) ore.push(h);
    return { base: base, fine: fine, ore: ore };
}

/**
 * Dove sta un intervallo dentro la griglia, in percentuale: quanto scende dal
 * bordo alto e quanto e' alto. Fuori dalla griglia non e' niente: le
 * occupazioni che sforano l'orario della sala si fermano al bordo.
 */
export function posizioneFascia(da, a, base, fine) {
    const cima = base * 60;
    const fondo = fine * 60;
    const dentroDa = Math.max(da, cima);
    const dentroA = Math.min(a, fondo);
    if (dentroA <= dentroDa) return null;
    const arco = fondo - cima;
    return {
        top: ((dentroDa - cima) / arco) * 100,
        altezza: ((dentroA - dentroDa) / arco) * 100
    };
}

/** Occupazioni che toccano il giorno, con inizio/fine in minuti. */
export function occupazioniDelGiorno(occupazioni, key) {
    const apre = inizioGiornata(key);
    if (!apre) return [];
    const chiude = new Date(apre.getFullYear(), apre.getMonth(), apre.getDate() + 1);

    const out = [];
    (occupazioni || []).forEach(o => {
        const da = parseSql(o.inizio);
        const a = parseSql(o.fine);
        if (!da || !a) return;
        if (a <= apre || da >= chiude) return;
        out.push({
            ...o,
            da: Math.max(0, Math.round((da - apre) / 60000)),
            a: Math.min(1440, Math.round((a - apre) / 60000))
        });
    });
    return out.sort((x, y) => x.da - y.da);
}

/** Spazi liberi fra `apertura` e `chiusura`, tolti gli intervalli occupati. */
export function intervalliLiberi(occupate, apertura, chiusura) {
    const occupati = occupate
        .map(o => ({ da: Math.max(o.da, apertura), a: Math.min(o.a, chiusura) }))
        .filter(o => o.a > o.da)
        .sort((x, y) => x.da - y.da);

    const liberi = [];
    let cursore = apertura;
    occupati.forEach(o => {
        if (o.da > cursore) liberi.push({ da: cursore, a: o.da });
        if (o.a > cursore) cursore = o.a;
    });
    if (chiusura > cursore) liberi.push({ da: cursore, a: chiusura });
    return liberi;
}

/** Orari di inizio proponibili dentro uno spazio libero, data la durata. */
export function orariPossibili(intervallo, durataMinuti) {
    if (!intervallo || !durataMinuti) return [];
    const out = [];
    let t = Math.ceil(intervallo.da / STEP_MINUTI) * STEP_MINUTI;
    while (t + durataMinuti <= intervallo.a) {
        out.push(t);
        t += STEP_MINUTI;
    }
    return out;
}

/**
 * Tariffa che si applica a una durata: `ore` e' un numero di ore intere,
 * oppure 'giornata' per la giornata intera. Stessa regola del BookingEngine,
 * che resta quello che decide: qui serve solo a mostrare il prezzo prima.
 *
 * Le tariffe «slot» sono fasce («fino a N ore»): vale la piu' corta che
 * copre le ore chieste. Oltre l'ultima fascia, e per la giornata intera,
 * vale la tariffa «giornata»; se manca, quella durata non si prenota.
 */
export function tariffaPerDurata(tariffe, ore) {
    const tipo = t => (t.tipo && t.tipo.value !== undefined ? t.tipo.value : t.tipo);
    const giornata = (tariffe || []).find(t => tipo(t) === 'giornata') || null;
    if (ore === 'giornata') return giornata;

    const fascia = (tariffe || [])
        .filter(t => tipo(t) !== 'giornata' && parseInt(t.durata_ore, 10) >= ore)
        .sort((a, b) => parseInt(a.durata_ore, 10) - parseInt(b.durata_ore, 10))[0];
    return fascia || giornata;
}

/**
 * Durate che si possono scegliere nel giorno: le ore intere che entrano in
 * almeno uno spazio libero, piu' la giornata intera se la sala e' libera da
 * apertura a chiusura. Ognuna porta con se' la tariffa che le tocca; quelle
 * che nessuna tariffa copre restano fuori.
 */
export function durateProponibili(info, availability) {
    if (!info || !availability) return [];
    const sala = availability.sala || {};
    const apertura = oraInMinuti(sala.orario_apertura);
    const chiusura = oraInMinuti(sala.orario_chiusura);
    const tariffe = availability.tariffe || [];

    const out = [];
    for (let ore = 1; ore * 60 <= chiusura - apertura; ore++) {
        const minuti = ore * 60;
        const tariffa = tariffaPerDurata(tariffe, ore);
        if (tariffa && info.liberi.some(l => orariPossibili(l, minuti).length > 0)) {
            out.push({ value: ore, minuti: minuti, tariffa: tariffa });
        }
    }

    const tuttoLibero = info.liberi.some(l => l.da <= apertura && l.a >= chiusura);
    const giornata = tariffaPerDurata(tariffe, 'giornata');
    if (tuttoLibero && giornata) {
        out.push({ value: 'giornata', minuti: chiusura - apertura, tariffa: giornata });
    }
    return out;
}

/**
 * Fotografia di un giorno: occupazioni, spazi liberi e stato di sintesi.
 * Stati: 'passato' | 'chiuso' | 'pieno' | 'parziale' | 'libero'.
 */
export function analizzaGiorno(key, availability) {
    const sala = availability.sala || {};
    const apertura = oraInMinuti(sala.orario_apertura);
    const chiusura = oraInMinuti(sala.orario_chiusura);
    /* La festivita' non occupa la sala a chi puo' gia' prenotarla (chi
       amministra sempre, il condomino da 15 giorni prima: lo dice il server
       con `si_prenota`); agli altri la chiude, e il giorno non si apre. */
    const tutte = occupazioniDelGiorno(availability.occupazioni, key);
    const festivita = tutte.filter(o => o.tipo === 'blocco' && o.sotto_tipo === 'festivita');
    const occupate = tutte.filter(o => !(festivita.includes(o) && o.si_prenota));

    const primoGiorno = String(availability.prenotabile_dal || '').substring(0, 10);
    const ultimoGiorno = String(availability.prenotabile_fino_al || '').substring(0, 10);

    /* Nel primo giorno utile conta solo il tempo oltre l'anticipo minimo. */
    let inizioUtile = apertura;
    if (key === primoGiorno) {
        inizioUtile = Math.max(apertura, oraInMinuti(String(availability.prenotabile_dal).substring(11)));
    }

    const fuoriFinestra = key < primoGiorno || (ultimoGiorno && key > ultimoGiorno);
    const minima = DURATA_MINIMA;
    const liberi = fuoriFinestra ? [] : intervalliLiberi(occupate, inizioUtile, chiusura);

    let stato;
    if (key < primoGiorno) stato = 'passato';
    else if (fuoriFinestra) stato = 'chiuso';
    else if (!liberi.some(l => l.a - l.da >= minima)) stato = 'pieno';
    else if (occupate.length) stato = 'parziale';
    else stato = 'libero';

    return {
        key: key,
        stato: stato,
        occupate: occupate,
        liberi: liberi.filter(l => l.a - l.da >= minima),
        mie: occupate.filter(o => o.tipo === 'prenotazione' && o.mia),
        // festivita' e' il tipo del blocco, che arriva come `sotto_tipo`
        festivo: festivita.length > 0,
        festivoChiuso: festivita.some(o => !o.si_prenota),
        prenotabile: stato === 'libero' || stato === 'parziale'
    };
}

/**
 * Un giorno che non si prenota si apre lo stesso, in sola consultazione, se
 * c'e' qualcosa da vedere: chi amministra vede chi occupa la sala, il
 * condomino ritrova le sue. Non pero' nei giorni che devono ancora aprirsi
 * (oltre la finestra, o prima dell'apertura delle prenotazioni) ne' nelle
 * festivita' che per chi guarda non sono ancora prenotabili: li' non si
 * clicca affatto. `oggi` e' la chiave 'YYYY-MM-DD' del giorno corrente.
 */
export function consultabile(info, oggi) {
    if (!info || info.prenotabile || info.occupate.length === 0) return false;
    // festivita' non ancora aperta a chi guarda: rossa e ferma
    if (info.festivoChiuso) return false;
    const nonAncoraAperto = info.key > oggi && (info.stato === 'chiuso' || info.stato === 'passato');
    return !nonAncoraAperto;
}
